import { TOOL_NAMES } from 'js/src/agent/toolNames';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { fromJS as ImmutableFromJS } from 'immutable';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import { MediaRegistry } from '../registries/MediaRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import type { SlideBackgroundModel } from 'js/src/client/types.gen';
import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { ImmutableFromJS as ImmutableFromJSConsts, type ImmutableMap } from 'js/src/libs/consts';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setMediaAssetForSceneOperation } from 'js/src/luminary/luminary-operations';

export const setStickerSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The sticker asset name (e.g. sticker_1, sticker_2)'),
    media_id: z
        .string()
        .optional()
        .describe(
            'The ID of the sticker media to apply to the asset. Must be an ID from search results.',
        ),
});
export type SetStickerArgs = z.infer<typeof setStickerSchema>;

export class SetStickerTool extends VideoStateTool<typeof setStickerSchema> {
    protected override readonly schema = setStickerSchema;
    protected override readonly toolName = TOOL_NAMES.setSticker;
    protected override readonly toolDescription = `Set media for a specific sticker asset in a scene.

<example>
When asked to apply a sticker media to an asset:
Call ${TOOL_NAMES.setSticker} with the media_id from search results
</example>
`;

    public override customRenderer = SetStickerResultRenderer;

    public override getToolDisplayName(): string {
        return 'Setting sticker';
    }

    public get statusIcon(): string {
        return 'sentiment_satisfied';
    }

    public async execute(args: SetStickerArgs, context: ToolExecutionContext) {
        const { asset_name, media_id } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }
            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            if (!VideoData.isMediaAsset(asset) || !VideoData.isStickerAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not a sticker asset`;
            }

            if (media_id === undefined) {
                return 'Error: No media_id provided';
            }

            const mediaRegistry = MediaRegistry.getInstance();
            if (!mediaRegistry.hasItem(media_id)) {
                return `Error: Unknown media ${media_id}`;
            }

            const media_source_data = mediaRegistry.getMediaSourceData(media_id);
            if (!media_source_data) {
                return `Error: Unknown media ${media_id}`;
            }

            const { setMediaAssetForScene } = makeOperationsCallable(
                { setMediaAssetForSceneOperation },
                this.luminary,
                this.dispatch,
                this._getState,
            );

            const mediaSourceData = ImmutableFromJS({
                type: media_source_data.type,
                video: media_source_data.type === 'video' ? media_source_data : null,
                image: media_source_data.type === 'image' ? media_source_data : null,
                history_index_user_ids: null,
            }) as unknown as ImmutableMap<SlideBackgroundModel>;

            setMediaAssetForScene({
                sceneId: scene.id,
                mediaSourceData,
                targetAssetId: asset.id,
                isSticker: true,
                systemTriggered: false,
                parentActionGroup,
            });

            return `Success: Set media for asset ${asset_name} in scene ${sceneShortId}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting sticker: ${errorMessage}`;
        }
    }
}

const SetStickerResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const mediaId = args.media_id;
    const mediaRegistry = MediaRegistry.getInstance();
    const mediaData = mediaId ? mediaRegistry.getItem(mediaId) : null;
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
            {mediaData && (
                <div>
                    {React.createElement(BackgroundAsset, {
                        searchContext: null,
                        media: ImmutableFromJSConsts(mediaData),
                        media_id: 0,
                        result_index: 0,
                    })}
                </div>
            )}
        </div>
    );
};
